/**
 * The entries the per-loan Servicing menu can show, and the layout an admin saves for them.
 *
 * This registry is the single source of truth for a menu key and its label, because two screens
 * read it: `Servicing.tsx`, which builds the menu a servicer sees, and Admin > Configuration >
 * Global Settings > Servicing, which lets an admin order and hide the entries. Held apart from
 * both so neither can drift from the other - a label edited in one place used to leave the admin
 * screen naming an entry something the menu never showed.
 *
 * Deliberately imports nothing. The admin screen that configures the menu lives in the Admin tree,
 * so reaching back into it from here - for the loan-field helper that derives a tenant's custom
 * pages, say - would close an import cycle through the Admin barrel. Callers derive those pages
 * and pass them in instead.
 */
export interface ServicingMenuEntry {
  key: string
  title: string
  /** Permission the entry is gated on. Omitted where every servicer who can open the loan sees it. */
  permission?: string
  /** Entries that depend on a vendor being enabled rather than on a permission. */
  vendor?: 'Fci'
}

/**
 * In the order the screens are built in, which is the menu's default: related screens sit
 * together, borrower and property first because that is what a servicer opening a loan is usually
 * looking for. An admin reorders it per tenant; this stays the fallback and the order new entries
 * are appended in.
 */
export const servicingMenuEntries: ServicingMenuEntry[] = [
  // The loan application's own Borrower and Property sections, rendered in place. Same fields and
  // same records as origination, so an edit here is an edit there.
  { key: 'borrowerInformation', title: 'Borrower Information' },
  { key: 'propertyInformation', title: 'Property Information' },
  { key: 'servicingNotes', title: 'Servicing Information', permission: 'Loan.Servicing.ServicingInformation' },
  // Dated changes to the rate, P&I and escrow. Anyone who can see the loan's terms can see them.
  { key: 'rateChanges', title: 'Rate & Payment Changes', permission: 'Loan.Servicing.ServicingInformation' },
  { key: 'ach', title: 'ACH' },
  // Money collected IN by ACH debit, so it sits with ACH rather than with the payout screens.
  // Ungated like ACH itself: the routes need a signed-in user and nothing more, and a new
  // permission would be absent on every existing tenant, which is how a shipped screen stays
  // invisible. Page Permissions still hides it.
  { key: 'debitRequests', title: 'Debit Requests' },
  { key: 'trustLedger', title: 'Trust Ledger', permission: 'Loan.Servicing.TrustLedger' },
  { key: 'servicingDocument', title: 'Servicing Documents', permission: 'Loan.Servicing.Documents' },
  { key: 'statements', title: 'Statements', permission: 'Loan.Servicing.Documents' },
  // Year-end borrower forms and the servicing report sit with statements: all three are output
  // produced from the loan rather than data maintained on it. Left ungated to match the
  // configurable page, which applies no permission of its own; Page Permissions still hides them.
  { key: 'taxForms', title: 'Tax Forms' },
  { key: 'servicingReport', title: 'Servicing Report' },
  { key: 'loanExtensions', title: 'Loan Extensions', permission: 'Loan.Servicing.Extension' },
  // The people who own a slice of this loan. The menu key stays `participation` so deep links,
  // saved views and the field-lookup targets that name it keep working.
  { key: 'participation', title: 'Investors', permission: 'Loan.Servicing.Participation' },
  { key: 'defaultTracking', title: 'Default Tracking', permission: 'Loan.Servicing.DefaultTracking' },
  { key: 'creditReporting', title: 'Credit Reporting', permission: 'Servicing.CreditReporting' },
  { key: 'collectionCalls', title: 'Collections Calls', permission: 'Loan.Servicing.CollectionCalls' },
  // Both borrower conversations, texting and the portal's secure messages, as sub tabs of one
  // screen. The key stays `textMessages` so saved menu orders and deep links keep working, and
  // the entry is ungated because only its texting tab needs `Loan.Servicing.TextMessages` -
  // gating the whole screen on it would withhold portal messages from everyone who cannot text.
  { key: 'textMessages', title: 'Messages' },
  { key: 'notesForServicing', title: 'Servicing Notes' },
  { key: 'globalNotes', title: 'Notes', permission: 'Loan.Servicing.Notes' },
  { key: 'tasks', title: 'Tasks', permission: 'Task' },
  { key: 'invoice', title: 'Invoices' },
  { key: 'orderValuation', title: 'Order Valuation' },
  { key: 'payoff', title: 'Payoff', permission: 'Loan.Servicing.Payoff' },
  { key: 'reinstatement', title: 'Reinstatement', permission: 'Loan.Servicing.Reinstatement' },
  { key: 'budget', title: 'Budget & Scope of Work' },
  { key: 'loanPayments', title: 'Loan Payments' },
  { key: 'partyPayments', title: 'Party Payments', permission: 'Loan.Servicing.PartyPayments' },
  { key: 'paymentVouchers', title: 'Payment Vouchers', permission: 'Loan.Servicing.PaymentVouchers' },
  { key: 'loanCharges', title: 'Loan Charges', permission: 'Loan.Servicing.LoanCharges' },
  { key: 'penalties', title: 'Penalties', permission: 'Loan.Servicing.Penalties' },
  { key: 'contacts', title: 'Contacts' },
  { key: 'parties', title: 'Parties' },
  { key: 'fci', title: 'FCI', vendor: 'Fci' },
  { key: 'vouchers', title: 'Escrow Vouchers', permission: 'Loan.Servicing.Vouchers' },
  { key: 'mers', title: 'MERS #' },
  { key: 'aiProject', title: 'AI Project' },
]

/**
 * The entries a given user can see, as `key -> label`, before any admin layout is applied.
 */
export const buildServicingMenu = ({
  hasPermission,
  isFciEnabled,
  loanFieldSubMenus,
}: {
  // Matches `usePermissions`, which answers undefined where it has no opinion yet.
  hasPermission: (permission: string) => boolean | undefined
  isFciEnabled: boolean
  loanFieldSubMenus: Record<string, { title: string }>
}): Record<string, string> => {
  const menu: Record<string, string> = {}

  servicingMenuEntries.forEach(({ key, title, permission, vendor }) => {
    if (permission && !hasPermission(permission)) return
    if (vendor === 'Fci' && !isFciEnabled) return
    menu[key] = title
  })

  // Appended after the built-in entries, matching where tenants already order them in config.
  Object.entries(loanFieldSubMenus).forEach(([key, submenu]) => {
    menu[key] = submenu.title
  })

  return menu
}

/**
 * Every entry the menu could show for this tenant, ignoring permissions and vendors: what the
 * admin screen offers. Tenant-wide configuration should not be narrowed to one admin's own role,
 * or an entry they cannot see would silently drop out of the order they save.
 */
export const servicingMenuLabels = (loanFieldSubMenus: Record<string, { title: string }>): Record<string, string> => {
  const labels: Record<string, string> = {}
  servicingMenuEntries.forEach(({ key, title }) => (labels[key] = title))
  Object.entries(loanFieldSubMenus).forEach(([key, { title }]) => (labels[key] = title))
  return labels
}

/**
 * `keys` in the admin's saved order: the keys that order names, in its order, then everything it
 * does not name in the order it was given. A screen added in a later release, or a custom page a
 * tenant has just defined, therefore joins the end of the menu rather than disappearing from a
 * tenant that has saved an order.
 */
export const orderServicingMenuKeys = (keys: string[], order?: string[]): string[] => {
  const named = (Array.isArray(order) ? order : []).filter(
    (key, index, all) => keys.includes(key) && all.indexOf(key) === index,
  )
  return [...named, ...keys.filter((key) => !named.includes(key))]
}

/**
 * The menu an admin's layout leaves: ordered, with the entries they hid dropped.
 *
 * Hiding is presentation only - the screen stays reachable by URL, and Page Permissions is what
 * actually withholds one.
 */
export const applyServicingMenuLayout = (
  menu: Record<string, string>,
  order?: string[],
  visible?: Record<string, boolean>,
): Record<string, string> => {
  const layout: Record<string, string> = {}
  orderServicingMenuKeys(Object.keys(menu), order)
    .filter((key) => visible?.[key] !== false)
    .forEach((key) => (layout[key] = menu[key]))
  return layout
}
